/**
 * DemoEmergencyTable — Emergency alerts table with Clear action column.
 *
 * Structurally identical to the AlertsPage EmergencyTable but adds an Action
 * column with a Clear button per row. This is demo-only functionality —
 * the shared EmergencyTable intentionally does NOT support clearing emergency alarms.
 *
 * Uses the same na-* and dp-* CSS classes.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow } from '../../../libs.jsx';
import AlertStatusBadge from '../../events/alerts/components/AlertStatusBadge.jsx';
import AlertTypeBadge from '../../events/alerts/components/AlertTypeBadge.jsx';

function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const DemoEmergencyTable = ({ data, sortColumn, sortOrder, onSort, onClear, clearing }) => {
    useTick(1000);

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>;
    };

    const formatUtcToLocal = (utcStr) => {
        if (!utcStr) return '\u2014';
        try {
            const d = new Date(utcStr.endsWith('Z') ? utcStr : utcStr + 'Z');
            return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
        } catch { return utcStr; }
    };

    const renderResidents = (row) => {
        const residents = row.ResidentsObject;
        if (!residents || !Array.isArray(residents) || residents.length === 0) return '\u2014';
        return residents.map((r, i) => (
            <span key={i}>
                {r.resident_id ? (
                    <Link to={`/profiles/resident/${r.resident_id}`} className="ar-link">
                        {r.firstName || r.fName || ''} {r.lastName || r.lName || ''}
                    </Link>
                ) : (`${r.firstName || r.fName || ''} ${r.lastName || r.lName || ''}`)}
                {i < residents.length - 1 ? ', ' : ''}
            </span>
        ));
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <th className="dp-th" style={{ textAlign: 'center' }}>Action</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('EventDateTime')}>Event Time{sortIcon('EventDateTime')}</th>
                        <th className="dp-th">Duration</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('AlertType')}>Type{sortIcon('AlertType')}</th>
                        <th className="dp-th">Residents</th>
                        <th className="dp-th">Device / Serial</th>
                        <th className="dp-th">Location / Area</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('NearName')}>Near{sortIcon('NearName')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('AcceptUsername')}>Accepted{sortIcon('AcceptUsername')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('Status')}>Status{sortIcon('Status')}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={10}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">{'\u2713'}</div>
                                    <div className="na-empty__text">No active emergency alerts</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row) => (
                        <tr key={row.AlarmId} className="dp-tr na-row-border" style={{ borderLeftColor: row.AlertType === 'Emergency' ? '#e63757' : row.AlertType === 'Maintenance' ? '#ffc107' : '#0081C7' }}>
                            <td className="dp-td" style={{ textAlign: 'center' }}>
                                <button
                                    className="na-clear-btn"
                                    onClick={() => onClear(row.AlarmId)}
                                    disabled={clearing === row.AlarmId}
                                >
                                    {clearing === row.AlarmId ? 'Clearing...' : 'Clear'}
                                </button>
                            </td>
                            <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcToLocal(row.EventDateTime)}</td>
                            <td className="dp-td"><span className="na-duration">{durationToNow(row.EventDateTime)}</span></td>
                            <td className="dp-td">
                                <AlertTypeBadge alertType={row.AlertType} alertSubType={row.AlertSubType} />
                            </td>
                            <td className="dp-td">{renderResidents(row)}</td>
                            <td className="dp-td">
                                <div className="na-stack">
                                    {row.DeviceId ? (
                                        <Link to={`/profiles/inovonics/${row.DeviceId}`} className="ar-link">{row.DeviceUse || '\u2014'}</Link>
                                    ) : (<span>{row.DeviceUse || '\u2014'}</span>)}
                                    <span className="na-stack__sub">{row.SerialNumber || ''}</span>
                                </div>
                            </td>
                            <td className="dp-td">
                                <div className="na-stack">
                                    {row.LocationId ? (
                                        <Link to={`/profiles/area/${row.LocationId}`} className="ar-link">{row.LocationName || '\u2014'}</Link>
                                    ) : (<span>{row.LocationName || '\u2014'}</span>)}
                                    <span className="na-stack__sub">{row.LocationArea || ''}</span>
                                </div>
                            </td>
                            <td className="dp-td">{row.NearName || '\u2014'}</td>
                            <td className="dp-td">{row.AcceptUsername || '\u2014'}</td>
                            <td className="dp-td"><AlertStatusBadge status={row.Status} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default DemoEmergencyTable;
